// ================================================================
// src/pages/BookChapters.jsx - نسخه نهایی با نمایش پیشرفت و زمان مطالعه
// ================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ThemeProvider,
  ToastProvider,
  useToast,
  useTheme
} from './PrivateClassShared';
import './BookChapters.css';
import { engine } from '../core/books/storage.js';
import dataEngine from '../core/books/data.js';
import { progressManager } from '../core/books/logic.js';
// 🔽 CHANGED: helper برای تشخیص نیاز پایه به رشته
import { needsField as curriculumNeedsField } from '../core/education/curriculum.js';

// ================================================================
// تابع تبدیل رشته‌ی زمان به دقیقه (عدد)
// ================================================================
const parseDurationToMinutes = (duration) => {
  if (typeof duration === 'number') return duration;
  if (typeof duration === 'string') {
    const trimmed = duration.trim();
    if (trimmed.includes(':')) {
      const parts = trimmed.split(':');
      if (parts.length === 2) {
        const hours = parseInt(parts[0], 10);
        const mins = parseInt(parts[1], 10);
        if (!isNaN(hours) && !isNaN(mins)) {
          return hours * 60 + mins;
        }
      }
    } else {
      const num = parseInt(trimmed, 10);
      if (!isNaN(num)) return num;
    }
  }
  return 0;
};

// ================================================================
// تابع تبدیل دقیقه به فرمت خوانا (اصلاح‌شده)
// ================================================================
const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '۰ دقیقه';
  if (minutes < 60) return `${minutes} دقیقه`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const minsStr = mins.toString().padStart(2, '0');
  return `${hours}:${minsStr}`;
};

// ================================================================
// کامپوننت دایره پیشرفت
// ================================================================
const ProgressCircle = ({ progress, size = 48, strokeWidth = 4 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="progress-circle-wrapper" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.15)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#4a6cf7"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
        <text
          x={size / 2}
          y={size / 2 + 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={size * 0.3}
          fill="currentColor"
          fontWeight="700"
        >
          {Math.round(progress)}%
        </text>
      </svg>
    </div>
  );
};

// ================================================================
// داده‌های کتاب‌ها (همان قبلی)
// ================================================================
const BOOKS_DATA = {

};

const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1571266028243-e4a5f5d6a4f8?w=800&h=500&fit=crop";

// ================================================================
// کامپوننت آیکون درس (همان قبلی)
// ================================================================
const LessonIcon = ({ icon, blobId, name }) => {
  const [resolvedUrl, setResolvedUrl] = useState(icon || null);
  const [imageError, setImageError] = useState(false);
  const lessonName = name || 'درس';

  // اگر فقط blobId داریم، URL رو از IndexedDB بگیر
  useEffect(() => {
    if (!icon && blobId) {
      let cancelled = false;
      engine.blobs.getUrl(blobId)
        .then((url) => { if (!cancelled && url) setResolvedUrl(url); })
        .catch(() => {});
      return () => { cancelled = true; };
    }
    // اگر icon داده شدن، همون رو استفاده کن
    if (icon) setResolvedUrl(icon);
  }, [icon, blobId]);

  if (resolvedUrl && !imageError) {
    return (
      <img 
        src={resolvedUrl} 
        alt={lessonName}
        className="bc-lesson-icon"
        style={{ 
          width: '47px', 
          height: '47px', 
          borderRadius: '30%', 
          objectFit: 'cover',
          border: '2px solid rgba(255,255,255,0.1)',
          backgroundColor: '#f0f0f0'
        }}
        onError={() => setImageError(true)}
        loading="lazy"
      />
    );
  }

  const colors = ["#FF6B6B", "#4ECDC4", "#FFD93D", "#6C5CE7", "#A8E6CF", "#FF8A5C", "#74B9FF", "#FD79A8"];
  const hash = lessonName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const bgColor = colors[hash % colors.length];
  
  const iconMap = {
    "جمع": "fa-plus", "تفریق": "fa-minus", "ضرب": "fa-xmark", "عدد": "fa-hashtag",
    "فصل": "fa-book", "درس": "fa-graduation-cap", "مرور": "fa-rotate",
    "دایره": "fa-circle", "مربع": "fa-square", "مثلث": "fa-play",
    "مستطیل": "fa-rectangle-ad", "الگو": "fa-puzzle-piece", "شمارش": "fa-calculator",
    "چرخه": "fa-water", "ابر": "fa-cloud-rain", "باد": "fa-wind",
    "گیاه": "fa-leaf", "گل": "fa-flower",
    "جانور": "fa-dog", "پرنده": "fa-crow", "حشره": "fa-bug"
  };

  let iconClass = "fa-book";
  for (const [key, value] of Object.entries(iconMap)) {
    if (lessonName.includes(key)) {
      iconClass = value;
      break;
    }
  }

  return (
    <div className="bc-lesson-icon" style={{ 
      background: bgColor, 
      color: '#fff',
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.1rem',
      flexShrink: 0
    }}>
      <i className={`fas ${iconClass}`}></i>
    </div>
  );
};






// ================================================================
// تبدیل داده‌ی داینامیک از registry به فرمت سازگار با رندر فعلی
// ================================================================
const normalizeDynamicBook = (dynamicData) => {
  if (!dynamicData || !Array.isArray(dynamicData.chapters)) return null;
  return {
    title: dynamicData.title || '',
    subtitle: dynamicData.subtitle || '',
    description: dynamicData.description || '',
    _isDynamic: true,
    _projectId: dynamicData.projectId,
    _teacherId: dynamicData.teacherId,
    chapters: dynamicData.chapters.map((ch, chIdx) => ({
      id: ch._id || `ch${chIdx + 1}`,
      title: ch.title || `فصل ${chIdx + 1}`,
      _dynamicId: ch._id,
      lessons: (ch.lessons || []).map((ls, lsIdx) => ({
        name: ls.title || `درس ${lsIdx + 1}`,
        icon: null,
        _blobId: ls.imageBlobId || null,
        _dynamicId: ls._id,
        _legacyLessonId: ls.legacyLessonId || null,
        _steps: Array.isArray(ls.steps) ? ls.steps : [],
      })),
    })),
  };
};







// ================================================================
// کامپوننت اصلی
// ================================================================
function BookChaptersContent() {
  const { grade, subject } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const showToast = useToast();
  const { theme, toggleTheme } = useTheme();

  // ===== بستن صفحه / بازگشت =====
  // اگه کاربر از داخل اپ اومده → برگرد به صفحه‌ی قبلی
  // اگه مستقیم وارد شده → برو به لیست کتاب‌ها (جلوگیری از صفحه‌ی سیاه)
  const handleBack = useCallback(() => {
    if (location.key === 'default' || window.history.length <= 1) {
      navigate('/books', { replace: true });
    } else {
      navigate(-1);
    }
  }, [location.key, navigate]);

  const [openChapters, setOpenChapters] = useState({});
  const [bookData, setBookData] = useState(null);
  const [teacherImage, setTeacherImage] = useState(DEFAULT_IMAGE);
  const [teacherName, setTeacherName] = useState('');
  const [teacherFolderName, setTeacherFolderName] = useState('');
  
  const [lessonProgressData, setLessonProgressData] = useState({});
  const [isLoadingProgress, setIsLoadingProgress] = useState(true);

  const userId = user?.id || 'unknown';

  // 🔽 CHANGED: استخراج رشته کاربر برای پایه‌های ۱۰-۱۲
  const userField = useMemo(() => {
    // اول از location.state (اگه از جای خاصی ناوبری شده)
    const fromState = location.state?.field || location.state?.teacher?.field;
    if (fromState) return String(fromState);

    // بعد از user خود کاربر
    const raw = user?.field || null;
    return raw ? String(raw) : null;
  }, [user?.field, location.state]);






  // ================================================================
// ✅ سیستم داینامیک - خواندن از registry
// ================================================================
const [dynamicTick, setDynamicTick] = useState(0);

useEffect(() => {
  const unsub1 = engine.on('registry:project:changed', () => setDynamicTick(t => t + 1));
  const unsub2 = engine.on('registry:version:changed', () => setDynamicTick(t => t + 1));
  const unsub3 = engine.on('registry:teacher:changed', () => setDynamicTick(t => t + 1));
  return () => {
    try { unsub1 && unsub1(); } catch {}
    try { unsub2 && unsub2(); } catch {}
    try { unsub3 && unsub3(); } catch {}
  };
}, []);

// folderName معلم - اول از state، بعد از teacher-map
const [resolvedFolder, setResolvedFolder] = useState(
  location.state?.teacher?.folderName || ''
);

useEffect(() => {
  if (resolvedFolder) return;
  try {
    // ✅ اول دیتابیس (منبع حقیقت)
    let tmap = null;
    if (
      user?.teacherMap &&
      typeof user.teacherMap === 'object' &&
      Object.keys(user.teacherMap).length > 0
    ) {
      tmap = user.teacherMap;
    }
    // ✅ اگر دیتابیس خالی بود، از engine
    if (!tmap) {
      tmap = engine.get(`teacher-map-${userId}`, null);
    }
    if (tmap) {
      const keys = Object.keys(tmap);
      if (keys.length === 1) {
        setResolvedFolder(keys[0]);
      }
    }
  } catch {}
}, [userId, resolvedFolder, user]);

// خواندن داده‌ی داینامیک
const dynamicBook = useMemo(() => {
  if (!resolvedFolder || !grade || !subject) return null;

  const gradeNeedsField = curriculumNeedsField(grade);

  // ===== پایه‌های ۱-۹: همان‌طور قبل =====
  if (!gradeNeedsField) {
    try {
      return dataEngine.query.getBookData(grade, subject, resolvedFolder, null);
    } catch {
      return null;
    }
  }

  // ===== پایه‌های ۱۰-۱۲ =====
  // ۱. اگه userField داریم، با همون امتحان کن
  if (userField) {
    try {
      const result = dataEngine.query.getBookData(grade, subject, resolvedFolder, userField);
      if (result) return result;
    } catch {}
  }

  // ۲. fallback: هر سه رشته رو امتحان کن (برای کاربران قدیمی که user.field ندارن)
  const FALLBACK_FIELDS = ['riazi', 'tajrobi', 'ensani'];
  for (const f of FALLBACK_FIELDS) {
    try {
      const result = dataEngine.query.getBookData(grade, subject, resolvedFolder, f);
      if (result) return result;
    } catch {}
  }

  // ۳. آخرین تلاش: بدون field (برای سازگاری با داده‌های قدیمی)
  try {
    return dataEngine.query.getBookData(grade, subject, resolvedFolder, null);
  } catch {
    return null;
  }
}, [grade, subject, resolvedFolder, dynamicTick, userField]);

const dynamicTeacher = useMemo(() => {
  if (!resolvedFolder) return null;
  try {
    return dataEngine.teacher.getByFolderName(resolvedFolder);
  } catch {
    return null;
  }
}, [resolvedFolder, dynamicTick]);





  // ================================================================
  // تابع خواندن اطلاعات پیشرفت یک درس (با parse زمان)
  // ================================================================
  const getLessonProgress = useCallback(async (lessonName, globalLessonIndex, lessonDynamicData) => {
  const folderName = teacherFolderName || resolvedFolder || 'default';
  const lessonId = lessonDynamicData?._legacyLessonId || `Lesson${globalLessonIndex + 1}`;
  const storageKey = `lesson-progress-${userId}-${grade}-${subject}-${folderName}-${lessonId}`;
  const projectId = bookData?._projectId || null;   // 🆕

  try {
    // ✅ اول دیتابیس (منبع حقیقت)، بعد localStorage (کش)
    let saved = null;
    try {
      if (user?.lessonProgress && user.lessonProgress[storageKey]) {
        saved = JSON.stringify(user.lessonProgress[storageKey]);
      }
    } catch (e) {
      // silent
    }
    if (!saved) {
      // 🆕 از engine (هماهنگ با remoteSync)
      const fromEngine = engine.get(storageKey, null);
      if (fromEngine) saved = JSON.stringify(fromEngine);
    }

    let completedSteps = [];
    let completedStepIds = [];
    if (saved) {
      const parsed = JSON.parse(saved);
      completedSteps = parsed.completedSteps || [];
      completedStepIds = parsed.completedStepIds || [];
    }

    let totalSteps = 0;
    let totalDuration = 0;
    let stepDurationsInMinutes = [];
    let isComplete = false;   // 🆕
    const dynamicSteps = lessonDynamicData?._steps;

    // اولویت ۱: از داده‌ی داینامیک
    if (Array.isArray(dynamicSteps) && dynamicSteps.length > 0) {
      totalSteps = dynamicSteps.length;
      stepDurationsInMinutes = dynamicSteps.map(step =>
        parseDurationToMinutes(step.durationMinutes || step.duration || 0)
      );
      totalDuration = stepDurationsInMinutes.reduce((sum, d) => sum + d, 0);

      if (completedStepIds.length > 0) {
        completedSteps = completedStepIds
          .map(id => dynamicSteps.findIndex(s => s._id === id))
          .filter(i => i >= 0);
      }

      // 🆕 چک کامل بودن درس: همه‌ی steps (آموزشی + آزمون) تکمیل شده باشند
      isComplete = true;
      for (const step of dynamicSteps) {
        if (step.type === 'quiz') {
          if (!projectId || userId === 'unknown') { isComplete = false; break; }
          const p = progressManager.getQuizStepProgress(userId, projectId, step._id);
          if (!p?.passed) { isComplete = false; break; }
        } else {
          if (!completedStepIds.includes(step._id)) { isComplete = false; break; }
        }
      }
    } else {
      // اولویت ۲: از فایل JSX (legacy)
      try {
        const module = await import(
          `../Lessons-Gam/${grade}/${subject}/${folderName}/${lessonId}.jsx`
        );
        const data = module.default || module;
        if (data?.steps?.length > 0) {
          totalSteps = data.steps.length;
          stepDurationsInMinutes = data.steps.map(step => parseDurationToMinutes(step.duration || 0));
          totalDuration = stepDurationsInMinutes.reduce((sum, d) => sum + d, 0);
          // 🆕 برای legacy: همه‌ی ایندکس‌ها باید در completedSteps باشد
          isComplete = totalSteps > 0 && completedSteps.length >= totalSteps;
        }
      } catch (error) {
        console.warn(`درس ${lessonId} یافت نشد:`, error);
      }
    }

    const completedCount = completedSteps.length;
    let studiedDuration = 0;
    completedSteps.forEach(stepIndex => {
      if (stepDurationsInMinutes[stepIndex]) {
        studiedDuration += stepDurationsInMinutes[stepIndex];
      }
    });

    const progress = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

    return {
      totalSteps,
      completedCount,
      progress,
      totalDuration,
      studiedDuration,
      hasData: totalSteps > 0,
      isComplete,   // 🆕
    };
  } catch (error) {
    console.warn(`خطا در خواندن پیشرفت درس ${lessonName}:`, error);
    return {
      totalSteps: 0,
      completedCount: 0,
      progress: 0,
      totalDuration: 0,
      studiedDuration: 0,
      hasData: false,
      isComplete: false,   // 🆕
    };
  }
}, [userId, grade, subject, teacherFolderName, resolvedFolder, bookData?._projectId, user]);











  const lessonCompletionMap = useMemo(() => {
    if (!bookData) return { chapters: [], lessons: [] };
    const chapters = [];
    const lessons = [];
    bookData.chapters.forEach((chapter) => {
      const lsStatus = [];
      chapter.lessons.forEach((lesson, li) => {
        const key = `${chapter.id}-lesson-${li}`;
        const info = lessonProgressData[key];
        lsStatus.push(!!info?.isComplete);
      });
      lessons.push(lsStatus);
      chapters.push(lsStatus.length > 0 && lsStatus.every(Boolean));
    });
    return { chapters, lessons };
  }, [bookData, lessonProgressData]);

  // فصل ci قفل است اگر فصل ci-1 کامل نشده باشد
  const isChapterLocked = useCallback((ci) => {
    if (ci === 0) return false;
    return !lessonCompletionMap.chapters[ci - 1];
  }, [lessonCompletionMap]);

  // درس li در فصل ci قفل است اگر:
  // - فصل قفل باشد، یا
  // - درس قبلی (همان فصل یا آخرین درس فصل قبل) کامل نشده باشد
  const isLessonLocked = useCallback((ci, li) => {
    if (isChapterLocked(ci)) return true;
    if (ci === 0 && li === 0) return false;

    if (li > 0) {
      return !lessonCompletionMap.lessons[ci]?.[li - 1];
    }
    const prevLessons = lessonCompletionMap.lessons[ci - 1] || [];
    if (prevLessons.length === 0) return true;
    return !prevLessons[prevLessons.length - 1];
  }, [isChapterLocked, lessonCompletionMap]);








  // ================================================================
  // بارگذاری اطلاعات پیشرفت همه درس‌ها
  // ================================================================
  useEffect(() => {

      // ================================================================
  // 🆕 محاسبه‌ی کامل بودن هر درس و هر فصل
  // ================================================================


    const loadAllProgress = async () => {
      if (!bookData || !user) {
        setIsLoadingProgress(false);
        return;
      }

      setIsLoadingProgress(true);
      const progressMap = {};
      let globalIndex = 0;

      for (const chapter of bookData.chapters) {
        for (let idx = 0; idx < chapter.lessons.length; idx++) {
          const lesson = chapter.lessons[idx];
          const key = `${chapter.id}-lesson-${idx}`;
          const data = await getLessonProgress(lesson.name, globalIndex, lesson);
          progressMap[key] = data;
          globalIndex++;
        }
      }

      setLessonProgressData(progressMap);
      setIsLoadingProgress(false);
    };

    loadAllProgress();
  }, [bookData, user, getLessonProgress]);

  // ================================================================
  // useEffect اولیه
  // ================================================================
  useEffect(() => {
  const state = location.state;
  
  // -------- بخش ۱: تنظیم معلم --------
  if (state?.teacher) {
    // از navigation state
    if (state.teacher.image) {
      setTeacherImage(state.teacher.image);
    } else if (state.teacher.avatarBlobId) {
      (async () => {
        try {
          const url = await engine.blobs.getUrl(state.teacher.avatarBlobId);
          if (url) setTeacherImage(url);
        } catch {}
      })();
    } else {
      setTeacherImage(DEFAULT_IMAGE);
    }
    setTeacherName(state.teacher.name || '');
    setTeacherFolderName(state.teacher.folderName || '');

    // ذخیره‌ی نقشه‌ی استاد — localStorage (کش) + دیتابیس (منبع حقیقت)
    try {
      const mapKey = `teacher-map-${userId}`;
      // 🆕 از engine
      let tmap = engine.get(mapKey, {}) || {};

      // ✅ اگر در دیتابیس هست، اون رو پایه بذار
      if (user?.teacherMap && typeof user.teacherMap === 'object') {
        tmap = { ...user.teacherMap, ...tmap };
      }

      const fn = state.teacher.folderName || 'default';
      if (fn && fn !== 'default') {
        tmap[fn] = {
          name: state.teacher.name || '',
          image: state.teacher.image || '',
        };

        // ✅ ذخیره از طریق engine → remoteSync → teacherMap (سرور)
        try {
          engine.set(mapKey, tmap);
        } catch (e) {
          console.warn('⚠️ ذخیره‌ی نقشه‌ی استاد ناموفق:', e);
        }
      }
    } catch (e) {
      console.warn('⚠️ ذخیره‌ی نقشه‌ی استاد ناموفق:', e);
    }
  } else if (dynamicTeacher) {
    // از registry پیدا شد
    setTeacherName(dynamicTeacher.fullName || '');
    setTeacherFolderName(dynamicTeacher.folderName || '');
    if (dynamicTeacher.avatarBlobId) {
      (async () => {
        try {
          const url = await engine.blobs.getUrl(dynamicTeacher.avatarBlobId);
          if (url) setTeacherImage(url);
        } catch {}
      })();
    } else if (dynamicTeacher.legacyAvatar) {
      setTeacherImage(dynamicTeacher.legacyAvatar);
    } else {
      setTeacherImage(DEFAULT_IMAGE);
    }
  } else {
    // پیش‌فرض
    setTeacherImage(DEFAULT_IMAGE);
    setTeacherName('');
    setTeacherFolderName('');
  }

  // -------- بخش ۲: بارگذاری کتاب --------
  // اولویت ۱: داینامیک
  const normalizedDynamic = normalizeDynamicBook(dynamicBook);
  if (normalizedDynamic && normalizedDynamic.chapters.length > 0) {
    setBookData(normalizedDynamic);
    const initialOpen = {};
    normalizedDynamic.chapters.forEach((ch) => { initialOpen[ch.id] = true; });
    setOpenChapters(initialOpen);
    return;
  }

  // اولویت ۲: legacy
  const key = `${grade}-${subject}`;
  const data = BOOKS_DATA[key];
  if (data) {
    setBookData(data);
    const initialOpen = {};
    data.chapters.forEach((ch) => { initialOpen[ch.id] = true; });
    setOpenChapters(initialOpen);
  } else {
    setBookData(null);
  }
}, [grade, subject, location, dynamicBook, dynamicTeacher, userId]);






  const toggleChapter = (chapterId) => {
    setOpenChapters(prev => ({ ...prev, [chapterId]: !prev[chapterId] }));
  };

  // ================================================================
  // تابع کلیک روی درس
  // ================================================================
  const handleLessonClick = (chapterId, lessonName, globalLessonIndex) => {
    const folderName = teacherFolderName || 'default';
    const lessonId = `Lesson${globalLessonIndex + 1}`;
    navigate(`/Lessons-Gam/${grade}/${subject}/${folderName}/${lessonId}`);
    showToast(`در حال بارگذاری ${lessonName}...`, 'info');
  };

  // ================================================================
  // رندر کارت درس
  // ================================================================
  const renderLessonCard = (lesson, idx, chapterId, globalLessonIndex, chapterIndex) => {
    const key = `${chapterId}-lesson-${idx}`;
    const progressInfo = lessonProgressData[key] || {
      totalSteps: 0,
      completedCount: 0,
      progress: 0,
      totalDuration: 0,
      studiedDuration: 0,
      hasData: false,
      isComplete: false,
    };

    const { totalSteps, completedCount, progress, studiedDuration } = progressInfo;
    const hasProgress = totalSteps > 0;
    const durationText = studiedDuration > 0 ? formatDuration(studiedDuration) : '';
    const locked = isLessonLocked(chapterIndex, idx);   // 🆕

    return (
      <div
        key={key}
        className={`bc-lesson-item ${locked ? 'bc-lesson-locked' : ''}`}
        onClick={() => {
          if (locked) {
            showToast('🔒 ابتدا درس قبلی را کامل کنید.', 'warning');
            return;
          }
          handleLessonClick(chapterId, lesson.name, globalLessonIndex);
        }}
      >
        {/* 🆕 برچسب قفل */}
        {locked && (
          <div className="bc-lesson-lock-badge">🔒</div>
        )}

        <LessonIcon icon={lesson.icon} blobId={lesson._blobId} name={lesson.name} />
        
        <div className="bc-lesson-info">
          <span className="bc-lesson-name">{lesson.name}</span>
          {hasProgress && (
            <div className="bc-lesson-stats">
              <span className="bc-lesson-steps">
                {completedCount}/{totalSteps} مرحله
              </span>
              {durationText && (
                <span className="bc-lesson-time">
                  <i className="fas fa-clock"></i> {durationText}
                </span>
              )}
            </div>
          )}
        </div>

        {hasProgress && !locked && (
          <div className="bc-lesson-progress">
            <ProgressCircle progress={progress} size={44} strokeWidth={3.5} />
          </div>
        )}

        <span className="bc-lesson-arrow">
          <i className={`fas ${locked ? 'fa-lock' : 'fa-chevron-left'}`}></i>
        </span>
      </div>
    );
  };

  if (!bookData) {
    return (
      <div className={`book-chapters-page ${theme === 'dark' ? 'dark-mode' : ''}`}>
        <div className="bc-empty-state">
          <span className="empty-icon">📚</span>
          <h3>کتابی یافت نشد</h3>
          <p>متأسفیم، اطلاعات این کتاب در دسترس نیست.</p>
          <button className="bc-back-btn" onClick={handleBack}>بازگشت</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`book-chapters-page ${theme === 'dark' ? 'dark-mode' : ''}`}>
      <div className="bc-hero" style={{ backgroundImage: `url(${teacherImage})` }}>
        <div className="bc-hero-gradient"></div>
        <div className="bc-hero-actions">
          <div className="bc-actions-left">
            <button className="bc-icon-btn" onClick={handleBack} title="بازگشت">
              <i className="fas fa-arrow-right"></i>
            </button>
          </div>
          <div className="bc-actions-right">
            <button className="bc-icon-btn" onClick={toggleTheme} title="تغییر تم">
              <i className={`fas ${theme === 'light' ? 'fa-moon' : 'fa-sun'}`}></i>
            </button>
            <Link to="/home" className="bc-icon-btn" title="صفحه اصلی">
              <i className="fas fa-home"></i>
            </Link>
          </div>
        </div>
        <div className="bc-hero-content">
          <h1 className="bc-book-title">{bookData.title}</h1>
          <div className="bc-book-subtitle">
            {bookData.subtitle} • <span className="highlight"> {teacherName || user?.name || 'کاربر'}</span>
          </div>
          <p className="bc-book-description">{bookData.description}</p>
        </div>
      </div>

      <div className="bc-divider"><div className="bc-divider-line"></div></div>

      <div className="bc-chapters-list">
        {bookData.chapters.map((chapter, chapterIndex) => {
          let startIndex = 0;
          for (let i = 0; i < chapterIndex; i++) {
            startIndex += bookData.chapters[i].lessons.length;
          }
          const chapterLocked = isChapterLocked(chapterIndex);   // 🆕
          return (
            <div
              key={chapter.id}
              className={`bc-chapter-item ${openChapters[chapter.id] ? 'open' : ''} ${chapterLocked ? 'bc-chapter-locked' : ''}`}
            >
              <div className="bc-chapter-header" onClick={() => toggleChapter(chapter.id)}>
                <div className="bc-chapter-number">
                  {chapterLocked ? '🔒' : chapterIndex + 1}
                </div>
                <h2 className="bc-chapter-title">{chapter.title}</h2>
                <span className="bc-chapter-arrow"><i className="fas fa-chevron-down"></i></span>
              </div>
              <div className="bc-lessons-grid">
                {chapter.lessons.map((lesson, idx) => {
                  const globalLessonIndex = startIndex + idx;
                  return renderLessonCard(lesson, idx, chapter.id, globalLessonIndex, chapterIndex);
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BookChapters() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <BookChaptersContent />
      </ToastProvider>
    </ThemeProvider>
  );
}